import { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { faFilter, faPlus, faSitemap } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import CreateDepartmentModal from 'components/modals/CreateDepartmentModal';
import DepartmentTable from 'components/devices/DepartmentTable';
import { Row, Col } from 'react-bootstrap';
import { fetchCompanies } from 'store/companySlice';
import { fetchUsers } from 'store/userSlice';
import { AppDispatch, RootState } from 'store';

const DepartmentManagement = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userRole = useSelector((state: RootState) => state.auth.role);
  const userCompanyId = useSelector((state: RootState) => state.auth.companyId);
  const { data: companies } = useSelector(
    (state: RootState) => state.companies
  );

  const [openCreate, setOpenCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const isSuperAdmin = useMemo(() => userRole === 'super_user', [userRole]);

  // Load users and companies when component mounts
  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchCompanies());
  }, [dispatch]);

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  // Determine the companyId for the CreateDepartmentModal
  const companyIdForModal =
    selectedCompanyId !== null
      ? selectedCompanyId
      : parseInt(userCompanyId || '1');

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon icon={faSitemap} className="me-2 text-primary" />
          Department Management
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
              />
              <Button
                variant="primary"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setOpenCreate(true)}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem',
                  fontWeight: '500'
                }}
              >
                Add Department
              </Button>
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && isSuperAdmin && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2">
              <CompanyFilter
                companies={companies}
                selectedCompanyId={selectedCompanyId}
                onCompanySelect={handleCompanySelect}
              />
            </div>
          </div>
        )}

        {/* Department Table */}
        <div className="mt-3">
          <DepartmentTable
            searchQuery={searchQuery}
            selectedCompanyId={selectedCompanyId}
          />
        </div>

        {/* Create Department Modal */}
        <CreateDepartmentModal
          show={openCreate}
          handleClose={() => setOpenCreate(false)}
          companyId={companyIdForModal}
        />
      </div>
    </div>
  );
};

export default DepartmentManagement;
